import { Check } from "lucide-react";
import { Icon, Sheet } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useActiveOrg } from "../../app/activeOrg.js";
import { subviewLabel } from "../../app/hooks/useRoster.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { Avatar } from "../mobile/Avatar.js";
import { ArtistMark } from "../../features/org/ArtistMark.js";
import { initials } from "../mobile/accountRoles.js";
import styles from "../mobile/MobileShell.module.scss";

type PersonSummary = components["schemas"]["PersonSummary"];
type OrgSeat = { orgId?: string | null; displayName?: string | null; root?: string | null };
type RosterArtistView = components["schemas"]["RosterArtistView"];

export interface RosterAccountSheetProps {
  person: PersonSummary | undefined;
  /** The active org — the checked row + the one excluded from "switch". */
  activeOrgId: string | undefined;
  /** Every org seat the person holds (from `useResolvedOrg`), for the switch list. */
  orgSeats: OrgSeat[];
  /** The SWITCH ARTIST list (ART-358 / ART-361 / ART-360) — every roster lens, whose phone dashboard is one
   *  artist at a time. Undefined ⇒ no list. */
  artists?: readonly RosterArtistView[];
  activeArtistId?: string;
  /** The org's name, the artist rows' meta line ("Booking · Frontline Booking"). */
  orgName?: string;
  subview?: string;
  onSelectArtist?: (bandId: string) => void;
  /** `roster:manage_seats` — gates the Team row and the Organization-vs-Account settings label. */
  canManageTeam: boolean;
  onViewProfile: () => void;
  onOpenTeam: () => void;
  onOpenSettings: () => void;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  onClose: () => void;
}

/**
 * The roster's mobile account sheet — the org-lens counterpart of the band `AccountSheet`, so the mobile
 * navbar's right side is the same shape on both shells (bell + avatar → a bottom sheet), not a desktop-style
 * name+role dropdown chip. A profile card (name · email · EDIT ›), then the Switch-organization list (every
 * org seat, the active one checked), then the foot: Team (admin only), Settings, Log out. Team/Settings open
 * the shell's modals — the same doors the desktop `RosterAccountChip` offers, in the mobile-native surface.
 */
export function RosterAccountSheet({
  person,
  activeOrgId,
  orgSeats,
  artists,
  activeArtistId,
  orgName,
  subview,
  onSelectArtist,
  canManageTeam,
  onViewProfile,
  onOpenTeam,
  onOpenSettings,
  onNavigate,
  onLogout,
  onClose,
}: RosterAccountSheetProps) {
  const setActiveOrg = useActiveOrg((s) => s.setActiveOrg);
  const displayName = person?.displayName || person?.email || "You";
  const email = person?.email ?? "";
  const selfAvatarUrl = useAvatarUrl("person", person?.personId, person?.avatarMediaId);
  const orgs = orgSeats.filter((s) => s.orgId);

  return (
    <Sheet title="Account" onClose={onClose} closeLabel="Close account menu">
      <button
        type="button"
        className={styles.accountProfile}
        onClick={() => {
          onClose();
          onViewProfile();
        }}
      >
        <Avatar url={selfAvatarUrl} mark={initials(displayName)} large />
        <span className={styles.accountProfileText}>
          <span className={styles.accountName}>{displayName}</span>
          {email && <span className={styles.accountEmail}>{email}</span>}
        </span>
        <span className={styles.accountEdit} aria-hidden="true">
          EDIT ›
        </span>
      </button>

      {artists && artists.length > 0 && (
        <>
          <p className={styles.accountEyebrow}>Switch artist</p>
          <ul className={styles.sheetList}>
            {[...artists]
              .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }))
              .map((a) => {
                const active = a.bandId === activeArtistId;
                return (
                  <li key={a.bandId}>
                    <button
                      type="button"
                      className={styles.accountRow}
                      aria-current={active ? "true" : undefined}
                      onClick={() => {
                        onClose();
                        if (!active && a.bandId) onSelectArtist?.(a.bandId);
                      }}
                    >
                      <ArtistMark name={a.name} bandId={a.bandId} avatarMediaId={a.avatarMediaId} size="md" />
                      <span className={styles.accountRowText}>
                        <span className={styles.accountRowName}>{a.name || "Artist"}</span>
                        <span className={styles.accountRowSub}>
                          {[subviewLabel(subview), orgName].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                      {active && (
                        <span className={styles.accountCheck}>
                          <Icon icon={Check} size={20} />
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
          </ul>
        </>
      )}

      {orgs.length > 1 && (
        <>
          <p className={styles.accountEyebrow}>Switch organization</p>
          <ul className={styles.sheetList}>
            {orgs.map((s) => {
              const active = s.orgId === activeOrgId;
              return (
                <li key={s.orgId}>
                  <button
                    type="button"
                    className={styles.accountRow}
                    aria-current={active ? "true" : undefined}
                    onClick={() => {
                      onClose();
                      if (active) return;
                      setActiveOrg(s.orgId as string);
                      onNavigate("/roster");
                    }}
                  >
                    <span className={styles.accountRowText}>
                      <span className={styles.accountRowName}>{s.displayName || "Organization"}</span>
                      <span className={styles.accountRowSub}>{subviewLabel(s.root)}</span>
                    </span>
                    {active && (
                      <span className={styles.accountCheck}>
                        <Icon icon={Check} size={20} />
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <div className={styles.accountFoot}>
        {/* Team is an ADMIN surface (roster:manage_seats) — absent, never disabled, without it. */}
        {canManageTeam && (
          <button
            type="button"
            className={styles.accountFootLink}
            onClick={() => {
              onClose();
              onOpenTeam();
            }}
          >
            Team
          </button>
        )}
        {/* The label follows the tabs behind it — the Account tab is a non-admin seat's only password door. */}
        <button
          type="button"
          className={styles.accountFootLink}
          onClick={() => {
            onClose();
            onOpenSettings();
          }}
        >
          {canManageTeam ? "Organization settings" : "Account settings"}
        </button>
        <button type="button" className={styles.accountFootLink} onClick={onLogout}>
          Sign out
        </button>
      </div>
    </Sheet>
  );
}
